import { useEffect, useRef, useState } from "react";
import { Check, Globe2, RefreshCw, Search, Send, Users, X } from "lucide-react";
import type { PublicGroup } from "../types";

type Props = Readonly<{
  onClose: () => void;
  onLoad: (query: string) => Promise<PublicGroup[]>;
  onRequestJoin: (group: PublicGroup) => Promise<void>;
}>;

export function GroupDirectoryModal({ onClose, onLoad, onRequestJoin }: Props) {
  const [query, setQuery] = useState("");
  const [groups, setGroups] = useState<PublicGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const loadSequence = useRef(0);

  const load = async (value: string) => {
    const sequence = ++loadSequence.current;
    setLoading(true);
    setError(null);
    try {
      const result = await onLoad(value);
      if (sequence === loadSequence.current) setGroups(result);
    } catch (caught) {
      if (sequence === loadSequence.current) setError(caught instanceof Error ? caught.message : "Не удалось загрузить открытые группы");
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => void load(query.trim()), query ? 300 : 0);
    return () => window.clearTimeout(timer);
  }, [query]);

  const requestJoin = async (group: PublicGroup) => {
    setBusyId(group.id);
    setError(null);
    setStatus(null);
    try {
      await onRequestJoin(group);
      setGroups((current) => current.map((item) => item.id === group.id ? { ...item, requested: true } : item));
      setStatus(`Запрос в группу «${group.name}» отправлен администраторам.`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось отправить запрос");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !busyId) onClose(); }} role="presentation">
      <section aria-labelledby="group-directory-title" aria-modal="true" className="modal-card group-directory" role="dialog">
        <header>
          <div><p className="eyebrow">КАТАЛОГ СООБЩЕСТВ</p><h2 id="group-directory-title">Открытые группы</h2></div>
          <button aria-label="Закрыть" className="icon-button" disabled={Boolean(busyId)} onClick={onClose} type="button"><X /></button>
        </header>
        <p className="directory-intro"><Globe2 /> Названия и описания открытых групп доступны в каталоге. Переписка остаётся зашифрованной и откроется только после одобрения заявки.</p>
        <div className="directory-search-row">
          <div className="search-box"><Search /><input autoFocus aria-label="Поиск открытых групп" onChange={(event) => setQuery(event.target.value)} placeholder="Название или описание" value={query} /></div>
          <button aria-label="Обновить каталог" className="icon-button" disabled={loading} onClick={() => void load(query.trim())} title="Обновить" type="button"><RefreshCw className={loading ? "spin-icon" : undefined} /></button>
        </div>
        {error && <div className="form-error" role="alert">{error}</div>}
        {status && <div className="form-success" role="status"><Check />{status}</div>}
        <div className="public-group-list">
          {!loading && groups.length === 0 && <div className="empty-group-directory"><Globe2 /><b>Группы не найдены</b><span>Измените запрос или создайте собственную открытую группу.</span></div>}
          {groups.map((group) => (
            <article className="public-group-row" key={group.id}>
              <span className="public-group-icon" style={{ background: group.avatarColor }}>{group.avatarUrl ? <img alt="" src={group.avatarUrl} /> : <Globe2 />}</span>
              <span className="public-group-copy"><b>{group.name}</b><small>{group.description || "Без описания"}</small><em><Users />{group.memberCount.toLocaleString("ru-RU")} участников</em></span>
              <button className={`primary-button compact ${group.requested ? "secondary" : ""}`} disabled={Boolean(busyId) || group.requested} onClick={() => void requestJoin(group)} type="button">
                {group.requested ? <><Check />Запрос отправлен</> : busyId === group.id ? "Отправляем…" : <><Send />Вступить</>}
              </button>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
